import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import {DELIVERY_WASTE_REASON_CODE, RETURN_REASON_CODE, SERVER_URL} from '../../shared/utils/Constants';
import {isOrderUnitSingle, calculateActualOrderAmount, normalizeOrderUnitOfMeasure, deriveOrderBoxCount} from './PurchaseRequisitionActualOrderUtils';

dayjs.extend(customParseFormat);

export const UNKNOWN_SUPPLIER_KEY = '__UNKNOWN__';
export const UNKNOWN_SUPPLIER_LABEL = 'Unknown Supplier';

const BRANCH_TRADING_NAME_KEY = 'merchantBranchTradingName';
const BRANCH_BUSINESS_NAME_KEY = 'merchantBranchBusinessName';
let branchNamesLoadPromise = null;

function readCachedBranchNames() {
    try {
        const tradingName = (sessionStorage.getItem(BRANCH_TRADING_NAME_KEY)
            || localStorage.getItem(BRANCH_TRADING_NAME_KEY)
            || '').trim();
        const businessName = (sessionStorage.getItem(BRANCH_BUSINESS_NAME_KEY)
            || localStorage.getItem(BRANCH_BUSINESS_NAME_KEY)
            || '').trim();
        return {tradingName, businessName};
    } catch (e) {
        return {tradingName: '', businessName: ''};
    }
}

function cacheBranchNames(tradingName, businessName) {
    const trading = tradingName != null ? String(tradingName).trim() : '';
    const business = businessName != null ? String(businessName).trim() : '';
    try {
        if (trading) {
            sessionStorage.setItem(BRANCH_TRADING_NAME_KEY, trading);
            localStorage.setItem(BRANCH_TRADING_NAME_KEY, trading);
        }
        if (business) {
            sessionStorage.setItem(BRANCH_BUSINESS_NAME_KEY, business);
            localStorage.setItem(BRANCH_BUSINESS_NAME_KEY, business);
        }
    } catch (e) {
        // ignore storage errors
    }
    return {tradingName: trading, businessName: business};
}

/**
 * Loads and caches merchant branch trading/business name for PDF headers.
 */
export async function ensureMerchantBranchNamesLoaded() {
    const cached = readCachedBranchNames();
    if (cached.tradingName || cached.businessName) {
        return cached;
    }
    if (branchNamesLoadPromise) {
        return branchNamesLoadPromise;
    }
    const branchId = localStorage.getItem('merchantBranchId');
    if (GeneralUtils.isNullOrEmpty(branchId) || String(branchId) === '-1') {
        return cached;
    }
    branchNamesLoadPromise = fetch(
        SERVER_URL + 'crm/merchant/branch/storeInfo/merchantBranchId/' + encodeURIComponent(branchId),
        {headers: {'Content-Type': 'application/json'}}
    )
        .then(response => response.json())
        .then(response => {
            if (!response || response.type) {
                return readCachedBranchNames();
            }
            return cacheBranchNames(response.tradingName, response.businessName);
        })
        .catch(() => readCachedBranchNames())
        .finally(() => {
            branchNamesLoadPromise = null;
        });
    return branchNamesLoadPromise;
}

export function getMerchantBranchNames() {
    return readCachedBranchNames();
}

/**
 * Primary display label: trading name, else business name.
 */
export function getMerchantBranchDisplayName() {
    const {tradingName, businessName} = readCachedBranchNames();
    return tradingName || businessName || '';
}

export function getItemSupplierId(item) {
    // Selected/override supplier wins for PO grouping & checkout; otherwise current product supplier.
    if (!GeneralUtils.isNullOrEmpty(item?.supplierId)) {
        return item.supplierId;
    }
    return item?.currentSupplierId ?? null;
}

export function getItemSupplierName(item) {
    if (!GeneralUtils.isNullOrEmpty(item?.supplierId)) {
        if (!GeneralUtils.isNullOrEmpty(item?.supplierName)) {
            return item.supplierName;
        }
    }
    if (!GeneralUtils.isNullOrEmpty(item?.currentSupplierName)) {
        return item.currentSupplierName;
    }
    if (!GeneralUtils.isNullOrEmpty(item?.supplierName)) {
        return item.supplierName;
    }
    return null;
}

export function hasSelectedSupplierOverride(item) {
    if (GeneralUtils.isNullOrEmpty(item?.supplierId)) {
        return false;
    }
    if (GeneralUtils.isNullOrEmpty(item?.currentSupplierId)) {
        return true;
    }
    return Number(item.supplierId) !== Number(item.currentSupplierId);
}

export function getSupplierGroupTotalCost(group) {
    const items = group?.items || [];
    let total = 0;
    let hasValue = false;
    items.forEach(item => {
        const qty = Number(item?.actualOrderAmount);
        const cost = Number(item?.currentCost);
        if (!Number.isNaN(qty) && !Number.isNaN(cost)
            && item?.actualOrderAmount !== '' && item?.actualOrderAmount != null
            && item?.currentCost !== '' && item?.currentCost != null) {
            total += qty * cost;
            hasValue = true;
        }
    });
    return hasValue ? total : null;
}

export function formatSupplierTotalCost(total) {
    if (total == null || Number.isNaN(Number(total))) {
        return null;
    }
    return `£${Number(total).toFixed(2)}`;
}

export function findItemIndexInList(itemList, item) {
    const list = itemList || [];
    if (!item) {
        return -1;
    }
    if (item.id != null) {
        const byId = list.findIndex(i => i.id != null && i.id === item.id);
        if (byId >= 0) {
            return byId;
        }
    }
    if (!GeneralUtils.isNullOrEmpty(item.barcode)) {
        const byNewBarcode = list.findIndex(i => !i.id && i.barcode === item.barcode);
        if (byNewBarcode >= 0) {
            return byNewBarcode;
        }
        return list.findIndex(i => i.barcode === item.barcode);
    }
    return -1;
}

/**
 * True when another line already uses this barcode (same document).
 * Allows re-saving the row being edited (same id, or unsaved line re-open).
 */
export function isDuplicateBarcodeInList(itemList, itemData) {
    const barcode = String(itemData?.barcode || '').trim();
    if (!barcode) {
        return false;
    }
    const list = itemList || [];
    const editingId = itemData?.id != null && itemData.id !== '' ? Number(itemData.id) : null;

    for (const existing of list) {
        if (String(existing?.barcode || '').trim() !== barcode) {
            continue;
        }
        const existingId = existing?.id != null && existing.id !== '' ? Number(existing.id) : null;
        if (editingId != null) {
            if (existingId !== editingId) {
                return true;
            }
            continue;
        }
        // New / unsaved dialog: any saved line with this barcode is a duplicate.
        if (existingId != null) {
            return true;
        }
        // Unsaved line with same barcode → re-edit of that soft row (upsert merges); not a second entry.
    }
    return false;
}

export function upsertRequisitionItem(itemList, itemData) {
    const normalizedItem = normalizeItem(itemData);
    const list = [...(itemList || [])];
    let index = findItemIndexInList(list, normalizedItem);
    if (index >= 0) {
        list[index] = {...list[index], ...normalizedItem};
    } else {
        list.push({...normalizedItem, status: normalizedItem.status || 'ACTIVE'});
        index = list.length - 1;
    }
    return {itemList: list, index};
}

export function normalizeItem(item) {
    if (!item) {
        return item;
    }
    const currentSupplierId = item.currentSupplierId ?? null;
    const currentSupplierName = item.currentSupplierName ?? null;
    // supplierId/Name = optional override; do not copy over current supplier.
    const selectedSupplierId = item.supplierId ?? null;
    const selectedSupplierName = item.supplierName ?? null;
    let expiryDate = item.expiryDate || null;
    if (expiryDate && typeof expiryDate === 'string' && expiryDate.includes('T')) {
        expiryDate = dayjs(expiryDate).format('DD/MM/YYYY');
    } else if (expiryDate && /^\d{4}-\d{2}-\d{2}/.test(String(expiryDate))) {
        expiryDate = dayjs(expiryDate).format('DD/MM/YYYY');
    }
    return {
        ...item,
        longName: item.longName || item.itemName || null,
        currentSupplierId,
        currentSupplierName,
        supplierId: selectedSupplierId,
        supplierName: selectedSupplierName,
        supplierBssId: item.supplierBssId ?? null,
        mailSent: item.mailSent === true || item.mailSent === 'true',
        mailToEmail: item.mailToEmail || null,
        mailCcEmail: item.mailCcEmail || null,
        damagedQuantity: item.damagedQuantity ?? null,
        reasonCode: item.reasonCode ?? null,
        description: item.description != null ? String(item.description).slice(0, 50) : null,
        deliveredBoxCount: item.deliveredBoxCount ?? null,
        orderUnitOfMeasure: (item.orderUnitOfMeasure != null && String(item.orderUnitOfMeasure).trim() !== '')
            ? String(item.orderUnitOfMeasure).trim().toUpperCase()
            : 'SINGLE',
        expiryDate
    };
}

export function buildItemPayload(item, options = {}) {
    const normalized = normalizeItem(item);
    const {
        merchantBranchId,
        ...rest
    } = normalized;
    let expiryDate = normalized.expiryDate || null;
    if (expiryDate && typeof expiryDate === 'string' && expiryDate.includes('/')) {
        const parsed = dayjs(expiryDate, 'DD/MM/YYYY');
        expiryDate = parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    }

    let actualOrderAmount = normalized.actualOrderAmount;
    // DN / Return: recalculate from UOM (Case: boxes × box qty; Single: pieces only).
    if (options.recalcFromBoxes === true) {
        const recalculated = calculateActualOrderAmount(
            normalized.deliveredBoxCount,
            normalized.boxSize,
            normalized.orderUnitOfMeasure
        );
        if (recalculated != null) {
            actualOrderAmount = recalculated;
        }
    }

    return {
        ...rest,
        actualOrderAmount,
        currentSupplierId: normalized.currentSupplierId,
        currentSupplierName: normalized.currentSupplierName,
        supplierId: normalized.supplierId,
        supplierName: normalized.supplierName,
        supplierBssId: normalized.supplierBssId,
        damagedQuantity: normalized.damagedQuantity,
        reasonCode: normalized.reasonCode,
        description: normalized.description != null ? String(normalized.description).slice(0, 50) : null,
        deliveredBoxCount: normalized.deliveredBoxCount,
        orderUnitOfMeasure: normalized.orderUnitOfMeasure || 'SINGLE',
        expiryDate
    };
}

export function buildRequisitionPayload(requisition) {
    const status = requisition.requisitionStatus || 'OPEN';
    const isDnOrReturn = status === 'DELIVERY_NOTE' || status === 'RETURN';
    const payload = {
        id: requisition.id,
        merchantBranchId: Number(localStorage.getItem('merchantBranchId')),
        description: requisition.description,
        requisitionStatus: status,
        assignedPurchaseRequisitionId: requisition.assignedPurchaseRequisitionId ?? null,
        supplierId: requisition.supplierId ?? null,
        supplierName: requisition.supplierName ?? null,
        supplierBssId: requisition.supplierBssId ?? null,
        itemList: (requisition.itemList || []).map(item =>
            buildItemPayload(item, {recalcFromBoxes: isDnOrReturn})
        )
    };
    // New Return/DN must start Open; never send Closed from create draft.
    if (!payload.id && (payload.requisitionStatus === 'RETURN' || payload.requisitionStatus === 'DELIVERY_NOTE')) {
        payload.approved = false;
        payload.approvedDate = null;
    }
    return payload;
}

/** Build a new Return draft from DN waste lines (damagedQuantity > 0). */
export function buildReturnDraftFromDeliveryNote(deliveryNote, options = {}) {
    if (!deliveryNote?.id) {
        return null;
    }
    const purchaseOrderItems = options.assignedPurchaseOrder?.itemList
        || options.purchaseOrderItems
        || [];
    const wasteItems = buildReturnItemsFromDeliveryNote(deliveryNote.itemList, {
        purchaseOrderItems,
        wasteOnly: true,
        useWasteAsQty: true
    });
    if (!wasteItems.length) {
        return null;
    }
    return {
        id: null,
        description: deliveryNote.requisitionNo
            ? `Return from ${deliveryNote.requisitionNo}`
            : 'Return from Delivery Note',
        requisitionStatus: 'RETURN',
        approved: false,
        approvedDate: null,
        assignedPurchaseRequisitionId: deliveryNote.id,
        assignedPurchaseRequisitionNo: deliveryNote.requisitionNo || null,
        createDate: dayjs().format('DD/MM/YYYY'),
        itemList: wasteItems
    };
}

/**
 * Build Return item lines from a Delivery Note.
 * - wasteOnly: only lines with waste qty
 * - useWasteAsQty: true → return qty = waste (create-from-DN); false → return qty 0 (manual DN assign)
 *   When omitted: defaults to true if wasteOnly, else false.
 */
export function buildReturnItemsFromDeliveryNote(deliveryNoteItems, options = {}) {
    const wasteOnly = options.wasteOnly === true;
    const useWasteAsQty = options.useWasteAsQty !== undefined
        ? options.useWasteAsQty === true
        : wasteOnly;
    const purchaseOrderItems = options.purchaseOrderItems || [];
    const poByBarcode = {};
    (purchaseOrderItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode || poByBarcode[barcode]) {
            return;
        }
        poByBarcode[barcode] = item;
    });

    const items = [];
    (deliveryNoteItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        const wasteQty = toQtyNumber(item.damagedQuantity);
        const deliveredQty = toQtyNumber(item.actualOrderAmount);
        if (wasteOnly && wasteQty <= 0) {
            return;
        }
        // Skip empty DN lines when seeding all items.
        if (!wasteOnly && deliveredQty <= 0 && wasteQty <= 0) {
            return;
        }
        const returnQty = useWasteAsQty && wasteQty > 0 ? wasteQty : 0;
        const poItem = poByBarcode[barcode] || null;
        const supplierId = item.supplierId
            ?? item.currentSupplierId
            ?? poItem?.supplierId
            ?? poItem?.currentSupplierId
            ?? null;
        const supplierName = item.supplierName
            ?? item.currentSupplierName
            ?? poItem?.supplierName
            ?? poItem?.currentSupplierName
            ?? null;
        const supplierBssId = item.supplierBssId
            ?? poItem?.supplierBssId
            ?? null;
        const reasonCode = useWasteAsQty && wasteQty > 0
            ? (item.reasonCode || resolveWasteReturnReasonCode(item.expiryDate))
            : null;

        let boxSize = item.boxSize != null && String(item.boxSize).trim() !== ''
            ? Number(item.boxSize)
            : 1;
        if (!Number.isFinite(boxSize) || boxSize <= 0) {
            boxSize = 1;
        }
        // When qty comes from waste, keep box packing aligned (legacy: 1 × waste when needed).
        if (useWasteAsQty && wasteQty > 0 && (item.boxSize == null || String(item.boxSize).trim() === '')) {
            boxSize = 1;
        }
        const deliveredBoxCount = returnQty > 0
            ? (() => {
                const boxes = returnQty / boxSize;
                return Number.isFinite(boxes) && boxes > 0
                    ? (Number.isInteger(boxes) ? boxes : Math.ceil(boxes))
                    : 1;
            })()
            : 0;

        items.push(normalizeItem({
            id: null,
            barcode: item.barcode,
            itemName: item.itemName,
            longName: item.longName || item.itemName,
            webImagePath: item.webImagePath || null,
            itemBssId: item.itemBssId,
            itemUnitMeasure: item.itemUnitMeasure,
            itemSize: item.itemSize,
            boxSize,
            supplierId,
            supplierName,
            supplierBssId,
            currentSupplierId: supplierId,
            currentSupplierName: supplierName,
            currentCost: item.currentCost ?? poItem?.currentCost ?? null,
            actualOrderAmount: returnQty,
            damagedQuantity: useWasteAsQty && wasteQty > 0 ? wasteQty : null,
            reasonCode,
            description: useWasteAsQty && item.description != null
                ? String(item.description).slice(0, 50)
                : null,
            deliveredBoxCount: useWasteAsQty && wasteQty > 0 && (item.boxSize == null || String(item.boxSize).trim() === '')
                ? wasteQty
                : deliveredBoxCount,
            expiryDate: reasonCode === 'EXPIRED' ? (item.expiryDate || null) : null,
            status: 'ACTIVE'
        }));
    });
    return items;
}

/** Waste → Return reason: expiry on/before today → EXPIRED, otherwise DAMAGED. */
export function resolveWasteReturnReasonCode(expiryDate) {
    if (!expiryDate) {
        return 'DAMAGED';
    }
    const parsed = typeof expiryDate === 'string' && expiryDate.includes('/')
        ? dayjs(expiryDate, 'DD/MM/YYYY')
        : dayjs(expiryDate);
    if (!parsed.isValid()) {
        return 'DAMAGED';
    }
    return parsed.isAfter(dayjs(), 'day') ? 'DAMAGED' : 'EXPIRED';
}

export function hasWasteItems(requisition) {
    return (requisition?.itemList || []).some(item => toQtyNumber(item.damagedQuantity) > 0);
}

/**
 * Return created from DN waste (Create Return / Approve & Create Return).
 * Those lines keep damagedQuantity copied from DN; manual DN-assign seeds do not.
 */
export function isReturnItemsLockedFromDeliveryNote(requisition) {
    if (!requisition || requisition.requisitionStatus !== 'RETURN') {
        return false;
    }
    if (GeneralUtils.isNullOrEmpty(requisition.assignedPurchaseRequisitionId)) {
        return false;
    }
    return hasWasteItems(requisition);
}

export function hasWasteInComparisonRows(comparisonRows) {
    return (comparisonRows || []).some(row => toQtyNumber(row.damagedQty) > 0);
}

export function shouldOfferCreateReturn(deliveryNote, comparisonRows) {
    return hasWasteItems(deliveryNote) || hasWasteInComparisonRows(comparisonRows);
}

export function hasUnknownSupplierItems(itemList) {
    return (itemList || []).some(item => GeneralUtils.isNullOrEmpty(getItemSupplierId(item)));
}

export async function buildCheckoutPayload(requisition, checkoutOptions) {
    await ensureMerchantBranchNamesLoaded();
    const payload = buildRequisitionPayload(requisition);
    const groups = groupItemsBySupplier(requisition.itemList);
    const checkoutDate = dayjs().format('DD/MM/YYYY');
    // Checkout turns document into Purchase Order — emails/PDFs should say PO.
    const pdfRequisition = {
        ...requisition,
        checkoutDate,
        requisitionStatus: 'PURCHASE_ORDER'
    };
    payload.checkoutSupplierOptions = groups.map(group => {
        const options = checkoutOptions[group.key] || {};
        const supplierOption = {
            supplierId: group.supplierId,
            supplierName: group.supplierName,
            mail: options.email === true,
            supplierEmail: options.supplierEmail || '',
            ccEmail: options.ccEmail || '',
            description: requisition.description,
            createDate: requisition.createDate,
            checkoutDate,
            requisitionNo: requisition.requisitionNo,
            requisitionStatus: 'PURCHASE_ORDER'
        };
        if (supplierOption.mail) {
            supplierOption.attachmentBase64 = buildSupplierPdfBase64(group, pdfRequisition);
            supplierOption.attachmentName = buildSupplierAttachmentName(group, pdfRequisition);
        }
        return supplierOption;
    });
    return payload;
}

export function normalizeRequisition(requisition) {
    if (!requisition) {
        return requisition;
    }
    const normalized = Object.assign({}, requisition);
    if (normalized.createDate) {
        normalized.createDate = dayjs(normalized.createDate).format('DD/MM/YYYY');
    }
    if (normalized.checkoutDate) {
        normalized.checkoutDate = dayjs(normalized.checkoutDate).format('DD/MM/YYYY');
    }
    if (normalized.approvedDate) {
        normalized.approvedDate = dayjs(normalized.approvedDate).format('DD/MM/YYYY');
    }
    normalized.approved = normalized.approved === true || normalized.approved === 'true';
    normalized.itemList = (normalized.itemList || []).map(normalizeItem);
    return normalized;
}

export function persistRequisitionToDb(requisition, purchaseRequisitionService) {
    const itemList = requisition.itemList || [];
    const status = requisition?.requisitionStatus;
    const allowEmptyDraft = status === 'DELIVERY_NOTE' || status === 'RETURN';
    if (itemList.length === 0 && !allowEmptyDraft) {
        return Promise.resolve({success: false, reason: 'empty'});
    }
    const payload = buildRequisitionPayload(requisition);
    const apiCall = payload.id
        ? purchaseRequisitionService.update(payload)
        : purchaseRequisitionService.create(payload);
    return apiCall.then(response => {
        if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
            return {success: false};
        }
        const merged = {
            ...response,
            supplierId: response.supplierId != null ? response.supplierId : requisition.supplierId,
            supplierName: response.supplierName != null ? response.supplierName : requisition.supplierName,
            supplierBssId: response.supplierBssId != null ? response.supplierBssId : requisition.supplierBssId
        };
        return {success: true, requisition: normalizeRequisition(merged)};
    }).catch(() => ({success: false}));
}

export function applyCostWarningsToItems(itemList, warnings) {
    const warningMap = {};
    (warnings || []).forEach(warning => {
        if (warning?.barcode) {
            warningMap[warning.barcode] = warning;
        }
    });
    return (itemList || []).map(item => {
        const warning = warningMap[item.barcode];
        if (!warning) {
            return normalizeItem(item);
        }
        return normalizeItem({
            ...item,
            cheaperCostAvailable: warning.cheaperCostAvailable === true,
            costAlternatives: warning.alternatives || []
        });
    });
}

export function buildCostWarningsMap(warnings) {
    const warningMap = {};
    (warnings || []).forEach(warning => {
        if (warning?.barcode) {
            warningMap[warning.barcode] = warning;
        }
    });
    return warningMap;
}

export function removeItemFromList(itemList, item) {
    const index = findItemIndexInList(itemList, item);
    if (index < 0) {
        return itemList || [];
    }
    return (itemList || []).filter((_, i) => i !== index);
}

const CHECKOUT_REQUIRED_FIELDS = [
    {key: 'barcode', label: 'Barcode', check: (item) => GeneralUtils.isNullOrEmpty(item.barcode)},
    {key: 'itemName', label: 'Product Name', check: (item) => GeneralUtils.isNullOrEmpty(item.itemName)},
    {
        key: 'supplier',
        label: 'Supplier',
        check: (item) => GeneralUtils.isNullOrEmpty(getItemSupplierId(item))
    },
    {
        key: 'actualOrderAmount',
        label: 'Total Pieces',
        check: (item) => isActualOrderAmountMissing(item.actualOrderAmount)
    }
];

function isActualOrderAmountMissing(value) {
    if (value == null || value === '') {
        return true;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return true;
    }
    return false;
}

export function formatCheckoutValidationErrors(errors) {
    if (!errors?.length) {
        return null;
    }
    if (errors.length === 1) {
        return errors[0];
    }
    const preview = errors.slice(0, 2).join(' · ');
    return errors.length > 2
        ? `${preview} (${errors.length - 2} more validation error(s))`
        : preview;
}

export function validateItemsForCheckout(itemList) {
    const errors = [];
    groupItemsBySupplier(itemList).forEach(group => {
        const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
        (group.items || []).forEach(rawItem => {
            const item = normalizeItem(rawItem);
            const barcode = item.barcode || '-';
            CHECKOUT_REQUIRED_FIELDS.forEach(field => {
                if (field.check(item)) {
                    errors.push(
                        `In supplier ${supplierName}, for product with barcode ${barcode}, ${field.label} is not entered`
                    );
                }
            });
        });
    });
    return errors;
}

export function buildInitialCheckoutOptions(itemList) {
    const options = {};
    groupItemsBySupplier(itemList).forEach(group => {
        options[group.key] = {
            supplierName: group.supplierName,
            supplierId: group.supplierId,
            print: false,
            email: false,
            supplierEmail: '',
            ccEmail: getLoggedInUserEmail()
        };
    });
    return options;
}

export function getRequisitionDocLabels(requisitionStatus, {forDetailPage = false, forMobile = false} = {}) {
    const detailSuffix = forDetailPage ? ' Detail' : '';
    const isReturn = requisitionStatus === 'RETURN';
    const isDelivery = requisitionStatus === 'DELIVERY_NOTE';
    const isPurchaseOrder = requisitionStatus === 'PURCHASE_ORDER';

    if (isReturn) {
        return {
            docTitle: `Return Note${detailSuffix}`,
            docNoLabel: 'Return Note No'
        };
    }
    if (isDelivery) {
        return {
            docTitle: `Delivery Note${detailSuffix}`,
            docNoLabel: 'Delivery Note No'
        };
    }
    if (isPurchaseOrder) {
        return {
            docTitle: `Purchase Order${detailSuffix}`,
            docNoLabel: 'Purchase Order No'
        };
    }
    const defaultTitle = forMobile ? 'Requisition' : 'Purchase Requisition';
    return {
        docTitle: `${defaultTitle}${detailSuffix}`,
        docNoLabel: 'Requisition No'
    };
}

export function buildSupplierAttachmentName(group, requisition) {
    const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
    const safeName = supplierName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_') || 'Supplier';
    const status = requisition?.requisitionStatus;
    let prefix = 'PurchaseRequisition';
    if (status === 'RETURN') {
        prefix = 'ReturnNote';
    } else if (status === 'PURCHASE_ORDER' || status === 'DELIVERY_NOTE') {
        prefix = status === 'DELIVERY_NOTE' ? 'DeliveryNote' : 'PurchaseOrder';
    }
    return `${prefix}_${safeName}.pdf`;
}

export function buildSupplierPdfBase64(group, requisition, options = {}) {
    const doc = new jsPDF('portrait', 'pt', 'A4');
    appendSupplierPdfPage(doc, group, requisition, true, options);
    const dataUri = doc.output('datauristring');
    const commaIndex = dataUri.indexOf(',');
    return commaIndex >= 0 ? dataUri.substring(commaIndex + 1) : dataUri;
}

export async function buildSupplierPdfBase64Async(group, requisition, options = {}) {
    await ensureMerchantBranchNamesLoaded();
    return buildSupplierPdfBase64(group, requisition, options);
}

export function getLoggedInUserEmail() {
    try {
        const hsmUser = JSON.parse(localStorage.getItem('hsmUser'));
        return hsmUser?.email || '';
    } catch (e) {
        return '';
    }
}

export function isValidEmailAddress(email) {
    if (GeneralUtils.isNullOrEmpty(email)) {
        return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

export function validateCheckoutEmailOptions(checkoutOptions) {
    const errors = [];
    Object.keys(checkoutOptions || {}).forEach(groupKey => {
        const options = checkoutOptions[groupKey];
        if (!options?.email) {
            return;
        }
        const supplierName = options.supplierName || UNKNOWN_SUPPLIER_LABEL;
        if (!isValidEmailAddress(options.supplierEmail)) {
            errors.push(`Please enter a valid To email for ${supplierName}`);
        }
        if (!isValidEmailAddress(options.ccEmail)) {
            errors.push(`Please enter a valid CC email for ${supplierName}`);
        }
    });
    return errors;
}

export async function sendRequisitionSupplierEmail(group, requisition, checkoutOption, purchaseRequisitionService) {
    if (!checkoutOption?.email) {
        return {success: true};
    }
    if (!requisition?.id) {
        return {success: false};
    }
    const payload = {
        supplierId: group.supplierId,
        supplierName: group.supplierName,
        supplierEmail: checkoutOption.supplierEmail,
        ccEmail: checkoutOption.ccEmail,
        description: requisition.description,
        createDate: requisition.createDate,
        requisitionNo: requisition.requisitionNo,
        requisitionStatus: requisition.requisitionStatus,
        attachmentBase64: await buildSupplierPdfBase64Async(group, requisition),
        attachmentName: buildSupplierAttachmentName(group, requisition)
    };
    const response = await purchaseRequisitionService.sendSupplierEmail(requisition.id, payload);
    if (!response || response.ok === false || !GeneralUtils.isNullOrEmpty(response.type)) {
        return {success: false};
    }
    return {success: true};
}

export function wasSupplierEmailSent(requisition, group) {
    const status = requisition?.requisitionStatus;
    if (status !== 'PURCHASE_ORDER' && status !== 'RETURN') {
        return false;
    }
    return (group.items || []).some(item => item?.mailSent === true || item?.mailSent === 'true');
}

export function getSupplierMailDetails(group) {
    const item = (group.items || []).find(entry =>
        !GeneralUtils.isNullOrEmpty(entry?.mailToEmail) || !GeneralUtils.isNullOrEmpty(entry?.mailCcEmail)
    );
    if (!item) {
        return null;
    }
    return {
        to: item.mailToEmail || '',
        cc: item.mailCcEmail || ''
    };
}

export function getSupplierGroupKey(item) {
    const supplierId = getItemSupplierId(item);
    if (!GeneralUtils.isNullOrEmpty(supplierId)) {
        return String(supplierId);
    }
    const supplierName = getItemSupplierName(item);
    if (!GeneralUtils.isNullOrEmpty(supplierName)) {
        return supplierName;
    }
    return UNKNOWN_SUPPLIER_KEY;
}

export function getSupplierGroupLabel(item) {
    const supplierName = getItemSupplierName(item);
    return supplierName || UNKNOWN_SUPPLIER_LABEL;
}

export function groupItemsBySupplier(itemList) {
    const groups = new Map();
    (itemList || []).forEach(rawItem => {
        const item = normalizeItem(rawItem);
        const key = getSupplierGroupKey(item);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                supplierId: getItemSupplierId(item),
                supplierName: getSupplierGroupLabel(item),
                supplierBssId: item.supplierBssId,
                items: []
            });
        }
        groups.get(key).items.push(item);
    });
    return Array.from(groups.values()).sort((a, b) =>
        (a.supplierName || '').localeCompare(b.supplierName || '', undefined, {sensitivity: 'base'})
    );
}

export function getItemsInSupplierGroupOrder(itemList) {
    return groupItemsBySupplier(itemList).flatMap(group => group.items);
}

export function getNextItemInSupplierOrder(itemList, currentItem) {
    const orderedItems = getItemsInSupplierGroupOrder(itemList);
    const currentIndex = findItemIndexInList(orderedItems, currentItem);
    if (currentIndex < 0 || currentIndex + 1 >= orderedItems.length) {
        return null;
    }
    return orderedItems[currentIndex + 1];
}

function formatValue(value) {
    if (value == null || value === '') {
        return '';
    }
    return String(value);
}

const PDF_TABLE_HEAD = [[
    'Barcode',
    'Name',
    'Total Pieces',
    'How ordered'
]];

const PDF_DELIVERY_TABLE_HEAD = [[
    'Barcode',
    'Name',
    'Box Qty',
    'Boxes',
    'PO Pieces',
    'Qty',
    'Rejected',
    'Net Qty',
    'Status',
    'Reason',
    'Description'
]];

const PDF_RETURN_TABLE_HEAD = [[
    'Barcode',
    'Name',
    'Box Qty',
    'Boxes',
    'Return Qty',
    'Reason',
    'Description'
]];

function formatReasonCode(reasonCode) {
    if (!reasonCode) {
        return '';
    }
    const match = (DELIVERY_WASTE_REASON_CODE || []).find(option => option.value === reasonCode)
        || (RETURN_REASON_CODE || []).find(option => option.value === reasonCode);
    if (match?.name) {
        return match.name;
    }
    return String(reasonCode).replace(/_/g, ' ');
}

function formatPdfCost(value) {
    if (value == null || value === '') {
        return '';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
        return String(value);
    }
    return number.toFixed(2);
}

/** One readable line so Single 8 / 5 / 5 is not misread as pack math. */
function formatPdfHowOrdered(item) {
    const orderQty = item.deliveredBoxCount;
    const pcsPerBox = item.boxSize;
    if (isOrderUnitSingle(item.orderUnitOfMeasure)) {
        const pieces = orderQty != null && orderQty !== ''
            ? orderQty
            : item.actualOrderAmount;
        return `Single · ${formatValue(pieces)} piece${Number(pieces) === 1 ? '' : 's'}`;
    }
    const cases = orderQty != null && orderQty !== '' ? orderQty : '';
    const perBox = pcsPerBox != null && pcsPerBox !== '' ? pcsPerBox : '';
    if (cases !== '' && perBox !== '') {
        return `Case · ${formatValue(cases)} case${Number(cases) === 1 ? '' : 's'} × ${formatValue(perBox)} / box`;
    }
    return 'Case';
}

function mapItemsToPdfBody(items, mode = 'default') {
    return (items || []).map(item => {
        const longName = item.longName || item.itemNameWithSizeAndQuantity || item.itemName;
        if (mode === 'return') {
            return [
                formatValue(item.barcode),
                formatValue(longName),
                formatValue(item.boxSize),
                formatValue(item.deliveredBoxCount),
                formatValue(item.actualOrderAmount),
                formatReasonCode(item.reasonCode),
                formatValue(item.description || '')
            ];
        }
        if (mode === 'delivery') {
            const wasteQty = item.damagedQuantity;
            const hasWaste = wasteQty != null && String(wasteQty).trim() !== '' && Number(wasteQty) !== 0;
            const deliveredQty = toQtyNumber(item.actualOrderAmount);
            const netQty = deliveredQty - toQtyNumber(wasteQty);
            const isMissing = item.pdfMissingOnDn === true || item.matchStatus === 'MISSING';
            const orderedPieces = item.orderedQty != null && item.orderedQty !== ''
                ? toQtyNumber(item.orderedQty)
                : '';
            let statusLabel = '';
            if (isMissing) {
                statusLabel = 'Missing on DN';
            } else if (item.matchStatus === 'SHORT') {
                statusLabel = 'Short';
            } else if (item.matchStatus === 'OVER') {
                statusLabel = 'Over';
            } else if (item.matchStatus === 'UNEXPECTED') {
                statusLabel = 'Not on PO';
            }
            return [
                formatValue(item.barcode),
                formatValue(longName),
                formatValue(item.boxSize),
                formatValue(isMissing ? '' : item.deliveredBoxCount),
                formatValue(orderedPieces !== '' ? orderedPieces : ''),
                formatValue(isMissing ? 0 : item.actualOrderAmount),
                formatValue(hasWaste ? wasteQty : ''),
                formatValue(isMissing ? 0 : netQty),
                formatValue(statusLabel),
                formatValue(hasWaste ? formatReasonCode(item.reasonCode) : ''),
                formatValue(isMissing
                    ? (item.description || `Missing on DN (ordered ${orderedPieces} total pieces)`)
                    : (item.description || ''))
            ];
        }
        return [
            formatValue(item.barcode),
            formatValue(longName),
            formatValue(item.actualOrderAmount),
            formatPdfHowOrdered(item)
        ];
    });
}

export async function exportSupplierPdf(group, requisition, options = {}) {
    await ensureMerchantBranchNamesLoaded();
    const doc = new jsPDF('portrait', 'pt', 'A4');
    appendSupplierPdfPage(doc, group, requisition, true, options);
    doc.save(buildSupplierAttachmentName(group, requisition));
}

export async function exportBulkSupplierPdf(groups, requisition, options = {}) {
    await ensureMerchantBranchNamesLoaded();
    const list = (groups || []).filter(group => {
        const items = resolvePdfItemsForGroup(group, requisition, options);
        return items.length > 0;
    });
    if (list.length === 0) {
        return;
    }
    const doc = new jsPDF('portrait', 'pt', 'A4');
    list.forEach((group, index) => {
        appendSupplierPdfPage(doc, group, requisition, index === 0, options);
    });
    const docNo = requisition?.requisitionNo || 'document';
    const status = requisition?.requisitionStatus || 'PR';
    doc.save(`${status}_${docNo}_bulk.pdf`);
}

function appendSupplierPdfPage(doc, group, requisition, isFirstPage, options = {}) {
    if (!isFirstPage) {
        doc.addPage();
    }
    const marginLeft = 40;
    const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
    const requisitionNo = requisition?.requisitionNo || '';
    const orderDate = requisition?.checkoutDate || requisition?.approvedDate || '';
    const status = requisition?.requisitionStatus;
    const isReturn = status === 'RETURN';
    const isDelivery = status === 'DELIVERY_NOTE';
    const {docTitle: title, docNoLabel: noLabel} = getRequisitionDocLabels(status);
    const dateLabel = isReturn || isDelivery ? 'Date' : 'Order Date';
    const {tradingName, businessName} = getMerchantBranchNames();
    const hasTrading = !GeneralUtils.isNullOrEmpty(tradingName);
    const hasBusiness = !GeneralUtils.isNullOrEmpty(businessName);
    const businessDiffers = hasTrading && hasBusiness
        && businessName.toLowerCase() !== tradingName.toLowerCase();

    let headerY = 40;

    // Document block (title + refs) — standard commercial layout.
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, marginLeft, headerY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    headerY += 18;
    if (requisitionNo) {
        doc.text(`${noLabel}: ${requisitionNo}`, marginLeft, headerY);
        headerY += 16;
    }

    // Counterparty: supplier first, then buyer (ordering site) underneath.
    doc.text(`Supplier: ${supplierName}`, marginLeft, headerY);
    headerY += 16;
    if (hasTrading || hasBusiness) {
        // Prefer showing both when different; one line when only one exists or names match.
        if (hasTrading) {
            doc.text(`Trading Name: ${tradingName}`, marginLeft, headerY);
            headerY += 14;
        }
        if (hasBusiness && (businessDiffers || !hasTrading)) {
            doc.text(`Business Name: ${businessName}`, marginLeft, headerY);
            headerY += 14;
        }
    }
    if (orderDate) {
        doc.text(`${dateLabel}: ${orderDate}`, marginLeft, headerY);
        headerY += 16;
    }

    const tableMode = isReturn ? 'return' : (isDelivery ? 'delivery' : 'default');
    const tableHead = isReturn
        ? PDF_RETURN_TABLE_HEAD
        : (isDelivery ? PDF_DELIVERY_TABLE_HEAD : PDF_TABLE_HEAD);
    const pdfItems = resolvePdfItemsForGroup(group, requisition, options);

    doc.autoTable({
        startY: headerY + 8,
        head: tableHead,
        body: mapItemsToPdfBody(pdfItems, tableMode),
        margin: {left: marginLeft, right: marginLeft},
        styles: isDelivery ? {fontSize: 7, cellPadding: 2} : undefined,
        headStyles: isDelivery ? {fontSize: 7} : undefined
    });
}

function toQtyNumber(value) {
    if (value == null || value === '') {
        return 0;
    }
    const number = Number(value);
    return Number.isNaN(number) ? 0 : number;
}

/**
 * Compare Purchase Order vs Delivery Note by barcode using total pieces
 * (actualOrderAmount = order/total pieces already calculated with Single/Case UOM on the PO).
 */
export function buildDeliveryPoComparisonRows(purchaseOrderItems, deliveryNoteItems) {
    const poByBarcode = {};
    (purchaseOrderItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!poByBarcode[barcode]) {
            poByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                orderedQty: 0
            };
        }
        // PO total pieces (Single: order qty; Case: order qty × box qty) — never raw boxes alone.
        poByBarcode[barcode].orderedQty += toQtyNumber(item.actualOrderAmount);
        if (!poByBarcode[barcode].itemName && item.itemName) {
            poByBarcode[barcode].itemName = item.itemName;
        }
    });

    const dnByBarcode = {};
    (deliveryNoteItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!dnByBarcode[barcode]) {
            dnByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                deliveredQty: 0,
                damagedQty: 0,
                reasonCode: null
            };
        }
        // DN total pieces received (boxes × box qty on delivery).
        const delivered = toQtyNumber(item.actualOrderAmount);
        const damaged = toQtyNumber(item.damagedQuantity);
        dnByBarcode[barcode].deliveredQty += delivered;
        dnByBarcode[barcode].damagedQty += damaged;
        if (item.reasonCode) {
            dnByBarcode[barcode].reasonCode = item.reasonCode;
        }
        if (!dnByBarcode[barcode].itemName && item.itemName) {
            dnByBarcode[barcode].itemName = item.itemName;
        }
    });

    const barcodes = new Set([...Object.keys(poByBarcode), ...Object.keys(dnByBarcode)]);
    const statusOrder = {UNEXPECTED: 0, MISSING: 1, SHORT: 2, OVER: 3, MATCHED: 4};
    const rows = [];

    barcodes.forEach(barcode => {
        const po = poByBarcode[barcode];
        const dn = dnByBarcode[barcode];
        const orderedQty = po ? po.orderedQty : 0;
        const deliveredQty = dn ? dn.deliveredQty : 0;
        const damagedQty = dn ? dn.damagedQty : 0;
        const netQty = deliveredQty - damagedQty;
        let matchStatus = 'MATCHED';
        let warning = '';

        if (!po && dn) {
            matchStatus = 'UNEXPECTED';
            warning = 'Item was not ordered on Purchase Order';
        } else if (po && !dn) {
            matchStatus = 'MISSING';
            warning = `Ordered ${orderedQty} total pieces, nothing received`;
        } else if (deliveredQty < orderedQty) {
            matchStatus = 'SHORT';
            warning = `Ordered ${orderedQty} total pieces, received ${deliveredQty}`;
        } else if (deliveredQty > orderedQty) {
            matchStatus = 'OVER';
            warning = `Ordered ${orderedQty} total pieces, received ${deliveredQty}`;
        }

        rows.push({
            id: barcode,
            barcode,
            itemName: (dn && dn.itemName) || (po && po.itemName) || '',
            orderedQty,
            deliveredQty,
            damagedQty,
            reasonCode: dn?.reasonCode || null,
            netQty,
            difference: deliveredQty - orderedQty,
            matchStatus,
            warning
        });
    });

    rows.sort((a, b) => (statusOrder[a.matchStatus] ?? 9) - (statusOrder[b.matchStatus] ?? 9)
        || String(a.barcode).localeCompare(String(b.barcode)));
    return rows;
}

/**
 * Synthetic DN lines for PO barcodes with no DN match (MISSING on DN).
 * Used on printed Delivery Note reports.
 */
export function buildMissingOnDnItems(purchaseOrderItems, deliveryNoteItems) {
    const comparison = buildDeliveryPoComparisonRows(purchaseOrderItems, deliveryNoteItems);
    const missingRows = (comparison || []).filter(row => row.matchStatus === 'MISSING');
    if (missingRows.length === 0) {
        return [];
    }
    const poByBarcode = {};
    (purchaseOrderItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode || poByBarcode[barcode]) {
            return;
        }
        poByBarcode[barcode] = item;
    });
    return missingRows.map(row => {
        const source = poByBarcode[row.barcode] || {};
        let boxSize = source.boxSize != null && String(source.boxSize).trim() !== ''
            ? Number(source.boxSize)
            : 1;
        if (!Number.isFinite(boxSize) || boxSize <= 0) {
            boxSize = 1;
        }
        const orderedQty = toQtyNumber(row.orderedQty);
        return normalizeItem({
            id: null,
            barcode: row.barcode,
            itemName: row.itemName || source.itemName || null,
            longName: source.longName || row.itemName || source.itemName || null,
            webImagePath: source.webImagePath || null,
            itemBssId: source.itemBssId || null,
            itemUnitMeasure: source.itemUnitMeasure || null,
            itemSize: source.itemSize ?? null,
            boxSize,
            deliveredBoxCount: 0,
            actualOrderAmount: 0,
            recommendedOrderAmount: orderedQty,
            orderedQty,
            damagedQuantity: null,
            reasonCode: null,
            description: `Missing on DN (ordered ${orderedQty} total pieces)`,
            currentCost: source.currentCost ?? null,
            currentSupplierId: source.currentSupplierId ?? source.supplierId ?? null,
            currentSupplierName: source.currentSupplierName ?? source.supplierName ?? null,
            supplierId: null,
            supplierName: null,
            supplierBssId: source.supplierBssId ?? null,
            matchStatus: 'MISSING',
            pdfMissingOnDn: true,
            status: 'ACTIVE'
        });
    });
}

/**
 * DN lines plus missing-on-DN lines from an assigned Purchase Order.
 */
export function mergeDeliveryItemsWithMissingFromPurchaseOrder(deliveryNoteItems, purchaseOrderItems) {
    const dnItems = deliveryNoteItems || [];
    if (!purchaseOrderItems || purchaseOrderItems.length === 0) {
        return dnItems;
    }
    const missing = buildMissingOnDnItems(purchaseOrderItems, dnItems);
    if (missing.length === 0) {
        return dnItems;
    }
    return [...dnItems, ...missing];
}

function annotateDeliveryItemsWithPoPieces(deliveryNoteItems, purchaseOrderItems) {
    if (!purchaseOrderItems || purchaseOrderItems.length === 0) {
        return deliveryNoteItems || [];
    }
    const orderedByBarcode = {};
    (purchaseOrderItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        orderedByBarcode[barcode] = (orderedByBarcode[barcode] || 0) + toQtyNumber(item.actualOrderAmount);
    });
    return (deliveryNoteItems || []).map(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode || orderedByBarcode[barcode] == null) {
            return item;
        }
        return {...item, orderedQty: orderedByBarcode[barcode]};
    });
}

function resolvePdfItemsForGroup(group, requisition, options = {}) {
    const isDelivery = requisition?.requisitionStatus === 'DELIVERY_NOTE';
    const baseItems = group?.items || [];
    if (!isDelivery) {
        return baseItems;
    }
    const purchaseOrderItems = options.purchaseOrderItems || null;
    if (!purchaseOrderItems || purchaseOrderItems.length === 0) {
        return baseItems;
    }
    const fullDnList = options.deliveryNoteItems != null
        ? options.deliveryNoteItems
        : baseItems;
    const missingAll = buildMissingOnDnItems(purchaseOrderItems, fullDnList);
    const groupSupplierId = group?.supplierId;
    const documentSupplierId = options.documentSupplierId != null
        ? options.documentSupplierId
        : requisition?.supplierId;
    const missingForGroup = missingAll.filter(item => {
        const itemSid = getItemSupplierId(item);
        if (group?.key === 'unknown') {
            return GeneralUtils.isNullOrEmpty(itemSid)
                && (documentSupplierId == null || documentSupplierId === '');
        }
        if (GeneralUtils.isNullOrEmpty(itemSid)) {
            // Unassigned missing lines go only on the DN document supplier group (no duplication).
            if (documentSupplierId != null && documentSupplierId !== '') {
                return groupSupplierId != null
                    && Number(groupSupplierId) === Number(documentSupplierId);
            }
            return groupSupplierId == null || groupSupplierId === '';
        }
        if (groupSupplierId == null || groupSupplierId === '') {
            return false;
        }
        return Number(itemSid) === Number(groupSupplierId);
    });
    const annotated = annotateDeliveryItemsWithPoPieces(baseItems, purchaseOrderItems);
    // Annotate short/over from comparison for status column.
    const comparison = buildDeliveryPoComparisonRows(purchaseOrderItems, fullDnList);
    const statusByBarcode = {};
    (comparison || []).forEach(row => {
        if (row?.barcode) {
            statusByBarcode[String(row.barcode).trim()] = row.matchStatus;
        }
    });
    const withStatus = annotated.map(item => {
        const barcode = String(item?.barcode || '').trim();
        const matchStatus = statusByBarcode[barcode];
        if (!matchStatus || matchStatus === 'MATCHED') {
            return item;
        }
        return {...item, matchStatus};
    });
    return [...withStatus, ...missingForGroup];
}

export function countDeliveryPoComparisonIssues(rows) {
    return (rows || []).filter(row => row.matchStatus && row.matchStatus !== 'MATCHED').length;
}

/**
 * Create Delivery Note items from Purchase Order lines (first PO assignment).
 * DN total pieces start equal to PO total pieces (actualOrderAmount).
 */
export function buildDeliveryItemsFromPurchaseOrder(purchaseOrderItems, documentHeader = {}) {
    const supplierId = documentHeader.supplierId ?? null;
    const supplierName = documentHeader.supplierName ?? null;
    const supplierBssId = documentHeader.supplierBssId ?? null;
    const items = [];
    (purchaseOrderItems || []).forEach(poItem => {
        const barcode = String(poItem?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        const orderedQty = toQtyNumber(poItem.actualOrderAmount);
        if (orderedQty <= 0) {
            return;
        }
        let boxSize = poItem.boxSize != null && String(poItem.boxSize).trim() !== ''
            ? Number(poItem.boxSize)
            : 1;
        if (!Number.isFinite(boxSize) || boxSize <= 0) {
            boxSize = 1;
        }
        const orderUnitOfMeasure = normalizeOrderUnitOfMeasure(poItem.orderUnitOfMeasure);
        let deliveredBoxCount;
        if (isOrderUnitSingle(orderUnitOfMeasure)) {
            deliveredBoxCount = orderedQty;
        } else {
            const boxes = deriveOrderBoxCount(orderedQty, boxSize);
            deliveredBoxCount = boxes != null && boxes > 0 ? boxes : 1;
        }
        const supplierFromPo = poItem.supplierId ?? poItem.currentSupplierId ?? supplierId;
        const supplierNameFromPo = poItem.supplierName ?? poItem.currentSupplierName ?? supplierName;
        items.push(normalizeItem({
            id: null,
            barcode,
            itemName: poItem.itemName || null,
            longName: poItem.longName || poItem.itemName || null,
            webImagePath: poItem.webImagePath || null,
            itemBssId: poItem.itemBssId || null,
            itemUnitMeasure: poItem.itemUnitMeasure || null,
            itemSize: poItem.itemSize ?? null,
            boxSize,
            orderUnitOfMeasure,
            deliveredBoxCount,
            actualOrderAmount: orderedQty,
            recommendedOrderAmount: orderedQty,
            damagedQuantity: 0,
            reasonCode: null,
            description: null,
            currentCost: poItem.currentCost ?? null,
            minStock: poItem.minStock ?? null,
            maxStock: poItem.maxStock ?? null,
            currentStock: poItem.currentStock ?? null,
            currentSupplierId: supplierFromPo,
            currentSupplierName: supplierNameFromPo,
            supplierId: null,
            supplierName: null,
            supplierBssId: poItem.supplierBssId ?? supplierBssId,
            status: 'ACTIVE'
        }));
    });
    return items;
}

